/**
 * Empty state component for CCMS operations
 */

import { forwardRef, ReactNode } from 'react';
import { Card, Stack, Text, Title, Button } from '@/components/ui';
import { IconInbox } from '@tabler/icons-react';
import type { GradientVariant } from '../../types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

export interface CCMSEmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  gradientVariant?: GradientVariant;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * Empty state component for displaying when no data is available
 *
 * @example
 * ```tsx
 * <CCMSEmptyState
 *   title="No files found"
 *   description="Upload your first file to get started"
 *   actionLabel="Upload File"
 *   onAction={() => openUploadModal()}
 * />
 * ```
 */
export const CCMSEmptyState = forwardRef<HTMLDivElement, CCMSEmptyStateProps>(
  (props, ref) => {
    const {
      icon = <IconInbox size={48} />,
      title,
      description,
      actionLabel,
      onAction,
      gradientVariant = 'primary',
      size = 'md',
      className,
    } = props;

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const iconSizes = {
      sm: 32,
      md: 48,
      lg: 64,
    };

    const titleOrders = {
      sm: 4,
      md: 3,
      lg: 2,
    } as const;

    return (
      <Card
        ref={ref}
        variant="subtle"
        className={className}
        style={{
          padding: size === 'sm' ? '1.5rem' : size === 'md' ? '2rem' : '3rem',
        }}
      >
        <Stack gap="md" align="center">
          <div style={{ color: 'var(--mantine-color-gray-5)' }}>
            {icon && typeof icon === 'object' && 'type' in icon
              ? icon
              : <IconInbox size={iconSizes[size]} />}
          </div>

          <Stack gap="xs" align="center">
            <Title order={titleOrders[size]}>{title}</Title>
            {description && (
              <Text size="sm" c="dimmed" ta="center" style={{ maxWidth: 400, ...accessibilityStyles }}>
                {description}
              </Text>
            )}
          </Stack>

          {actionLabel && onAction && (
            <Button
              variant="gradient"
              gradientVariant={gradientVariant}
              onClick={onAction}
              mt="sm"
            >
              {actionLabel}
            </Button>
          )}
        </Stack>
      </Card>
    );
  }
);

CCMSEmptyState.displayName = 'CCMSEmptyState';

export default CCMSEmptyState;
